import React, { useState, useEffect } from "react";
import { generateRandomString } from "js/helpers/header";
import toast from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import TrashIcon from "@mui/icons-material/DeleteRounded";


import {
    fetchOrganizationUsers,
    inviteUser,
    deleteUser,
    updateUser,
    logout, CopyUserInviteLink
} from "js/Api/index";

function ManageUsers({ userInfo, selectedOrg, orgGuid, resetFlag }) {
    const [users, setUsers] = useState([]);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [newUser, setNewUser] = useState({ name: "", email: "", role: "" });
    const [deleteUserId, setDeleteUserId] = useState(null);
    const [showEditUserModal, setShowEditUserModal] = useState(false);
    const [editedUser, setEditedUser] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    //const isManager = userInfo?.role === "manager";
    const isStaff = userInfo?.role === "staff";
    const [emailError, setEmailError] = useState("");
    const [emailTouched, setEmailTouched] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchUsers(orgGuid);
    }, [userInfo, orgGuid]);

    useEffect(() => {

        setShowInviteModal(false);
        setNewUser({ name: "", email: "", role: "" });
        setDeleteUserId(null);
        setShowEditUserModal(false);
        setEditedUser(null);
        setShowDeleteModal(false);
    }, [resetFlag]);

    const fetchUsers = async (orgGuid) => {
        const result = await fetchOrganizationUsers(orgGuid);

        if (result.success) {
            const usersWithStatus = result.data.map((user) => {
                const passwordHash = user.passwordHash || "";
                const status = passwordHash ? "active" : "pending";
                return { ...user, status };
            });
            setUsers(usersWithStatus);
        } else {
            toast.error(result.error || "Failed to fetch users");
        }
    };

    const handleInviteClick = () => {
        setShowInviteModal(true);
    };

    const validateEmail = (email) => {
        const re =
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    };

    const handleUserInputChange = (e) => {
        const { name, value } = e.target;
        setNewUser({ ...newUser, [name]: value });
        if (name === "email") {
            setEmailTouched(true);
            if (!validateEmail(value)) {
                setEmailError("Please enter a valid email address");
            } else {
                setEmailError("");
            }
        }
    };

    const handleInviteSubmit = async (e) => {
        e.preventDefault();
        if (!validateEmail(newUser.email)) {
            setEmailError("Please enter a valid email address");
            return;
        }

        const userData = {
            organizationGuid: selectedOrg.id,
            name: newUser.name,
            email: newUser.email,
            role: newUser.role,
        };

        const result = await inviteUser(userData);

        if (result.success) {
            toast.success(result.message);
            setNewUser({ name: "", email: "", role: "" });
            setShowInviteModal(false);
            fetchUsers(orgGuid);
        } else {
            toast.error(result.error);
        }
    };

    const handleInviteCancel = () => {
        setShowInviteModal(false);
        setNewUser({ name: "", email: "", role: "" });
        setEmailError("");
        setEmailTouched(false);
    };

    const handleDeleteUserClick = (userId) => {
        if (userId) {
            setDeleteUserId(userId);
            setShowDeleteModal(true);
        }
    };

    const handleDeleteUserConfirm = async () => {
        const result = await deleteUser(deleteUserId);

        if (result.success) {
            const updatedUsers = users.filter((user) => user.userId !== deleteUserId);
            setUsers(updatedUsers);
            setDeleteUserId(null);
            setShowDeleteModal(false);
            toast.success(result.message);
            fetchUsers(orgGuid);

            if (deleteUserId === userInfo.userId) {
                // Perform logout
                await handleLogout();
            }
        } else {
            toast.error(result.message);
        }
    };

    const handleLogout = async () => {
        try {
            const response = await logout();

            if (response.success) {
                // Clear local storage
                localStorage.removeItem("email");
                localStorage.removeItem("password");
                localStorage.removeItem("rememberMe");
                localStorage.removeItem("isLoggedIn");
                localStorage.removeItem("userInfo");

                // Redirect to login page
                navigate("/");
                toast.success(
                    "Your account has been deleted. You have been logged out."
                );
            } else {
                console.log("Logout failed.");
                toast.error(
                    "Failed to log out. Please close your browser to complete the process."
                );
            }
        } catch (error) {
            console.error("Error logging out:", error);
            toast.error(
                "An error occurred during logout. Please close your browser to complete the process."
            );
        }
    };
    const handleDeleteUserCancel = () => {
        setShowDeleteModal(false);
    };

    const handleUserClick = (user) => {
        if (isStaff) return;
        setEditedUser(user);
        setShowEditUserModal(true);
    };

    const handleEditUserInputChange = (e) => {
        setEditedUser({ ...editedUser, [e.target.name]: e.target.value });
    };

    const handleEditUserSubmit = async (e) => {
        e.preventDefault();

        const userData = {
            name: editedUser.name,
            organizationGuid: selectedOrg.id,
            role: editedUser.role,
        };

        const result = await updateUser(editedUser.userId, userData);

        if (result.success) {
            const updatedUsers = users.map((user) =>
                user.userId === editedUser.userId ? editedUser : user
            );
            setUsers(updatedUsers);
            setShowEditUserModal(false);
            toast.success(result.message);
            fetchUsers(orgGuid);
        } else {
            toast.error(result.message);
        }
    };

    const handleEditUserCancel = () => {
        setShowEditUserModal(false);
    };

    const ClickUserInviteLink = async () => {
        const result = await CopyUserInviteLink(editedUser.userId)
        if (result.success) {
            let url = result.data.url;
            navigator.clipboard.writeText(url)
            toast.success("Invitation link copied to the clipboard");
        } else {
            toast.error(result.message);
        }
    }

    return (
        <div className="midde_cont">
            <div className="container-fluid" style={{ paddingLeft: "0", paddingRight: "0" }} >
                <div className="row column_title">
                    <div className="col-md-12">
                        <div className="page_title" style={{ display: "flex", alignItems: "center" }} >
                            <h2 style={{ flex: "1" }}>Manage Users</h2>
                        </div>
                    </div>
                    <div class="white_shd full margin_bottom_30">
                        <div class="full inner_elements">
                            <div class="padding_infor_info">
                                {isStaff ? null : (
                                    <button className="btn btn-outline-info btn-lg px-4 me-sm-3 fw-bold" onClick={handleInviteClick} >
                                        <b>Invite +</b>
                                    </button>
                                )}
                                <div class="clear"></div><br />
                                <table className="table table-hover">
                                    <thead>
                                        <tr key="headers">
                                            <th key="Name" width="15%">Name</th>
                                            <th key="Email">Email</th>
                                            <th key="Role" width="10%">Role</th>
                                            <th key="Status" width="10%">Status</th>
                                            {isStaff ? null : <th key="Action" width="5%"></th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map((user) => (
                                            <tr key={user.id} onClick={() => handleUserClick(user)}>
                                                <td>{user.name}</td>
                                                <td>{user.email}</td>
                                                <td>{user.role === "Staff" ? "Normal User": user.role}</td>
                                                <td className="status">
                                                    {user.passwordHash ? (
                                                        <span className="active">{user.status}</span>
                                                    ) : (
                                                        <span className="waiting">{user.status}</span>
                                                    )}
                                                </td>
                                                {isStaff ? null : (
                                                    <td>
                                                        <TrashIcon
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteUserClick(user.userId);
                                                            }}
                                                            sx={{
                                                                fontSize: "1rem", // 调整图标大小
                                                                color: "black", // 调整图标颜色
                                                                marginRight: "10px",
                                                                cursor: "pointer",
                                                            }}
                                                        />
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                {showInviteModal && (
                    <div className="modal" tabIndex={"-1"} role="dialog">
                        <div className="modal-dialog" role="document">
                            <div className="modal-content" style={{ width: "400px" }}>
                                <div className="modal-header">
                                    <h1 className="modal-title">Invite User</h1>
                                    <button className="close-button" onClick={handleInviteCancel}>
                                        &times;
                                    </button>
                                </div>
                                <div className="modal-body">
                                    <form className="contact-form">
                                        <div className="form-group">
                                            <label htmlFor="name">Name</label>
                                            <input
                                                className="form-control"
                                                type="text"
                                                id="name"
                                                name="name"
                                                value={newUser.name}
                                                onChange={handleUserInputChange}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="email">Email</label>
                                            <input
                                                className={`form-control ${emailError ? "is-invalid" : ""
                                                    }`}
                                                type="email"
                                                id="email"
                                                name="email"
                                                placeholder="example@xxx.com"
                                                value={newUser.email}
                                                onChange={handleUserInputChange}
                                                required
                                            />
                                            {emailError && (
                                                <div
                                                    className="invalid-feedback"
                                                    style={{ display: "block" }}
                                                >
                                                    {emailError}
                                                </div>
                                            )}
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="role">Role</label>
                                            <select
                                                className="form-control"
                                                id="role"
                                                name="role"
                                                value={newUser.role}
                                                onChange={handleUserInputChange}
                                                required
                                            >
                                                <option value="">Select a role</option>
                                                <option value="Manager">Manager</option>
                                                <option value="Staff">Normal User</option>
                                            </select>
                                        </div>
                                        <div className="modal-footer">
                                            <button
                                                type="button"
                                                className="btn cur-p btn-secondary"
                                                onClick={handleInviteCancel}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn cur-p btn-primary"
                                                onClick={handleInviteSubmit}
                                                disabled={
                                                    !newUser.name ||
                                                    !newUser.email ||
                                                    !newUser.role ||
                                                    !!emailError
                                                }
                                            >
                                                Invite
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {showDeleteModal && (
                    <div
                        className="modal fade show"
                        style={{ display: "block" }}
                        aria-modal="true"
                        role="dialog"
                    >
                        <div className="modal-dialog" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h1 className="modal-title">Delete User</h1>
                                    <button
                                        type="button"
                                        className="close-button"
                                        onClick={handleDeleteUserCancel}
                                        aria-label="Close"
                                    >
                                        <span aria-hidden="true">&times;</span>
                                    </button>
                                </div>
                                <div className="modal-body">
                                    <p>
                                        Are you sure you want to delete{" "}
                                        <strong>
                                            {users.find((user) => user.userId === deleteUserId)?.name}
                                        </strong>
                                        ?
                                    </p>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={handleDeleteUserCancel}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={handleDeleteUserConfirm}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {showEditUserModal && (
                    <div className="modal" role="dialog">
                        <div className="modal-dialog" role="document">
                            <div className="modal-content" style={{ width: "400px" }}>
                                <div className="modal-header">
                                    <h1 className=" modal-title">Edit User</h1>
                                    <button
                                        className="close-button"
                                        onClick={handleEditUserCancel}
                                    >
                                        &times;
                                    </button>
                                </div>
                                <form className="contact-form">
                                    <div className="form-group" style={{ position: "relative" }}>
                                        <div>
                                            <label htmlFor="staticEmail" className=" col-form-label">
                                                Email:
                                            </label>
                                            <input
                                                readOnly
                                                className="form-control-plaintext"
                                                id="staticEmail"
                                                type="text"
                                                name="email"
                                                value={editedUser.email}
                                                style={{
                                                    paddingLeft: "10px",
                                                    backgroundColor: "#e9ecef",
                                                }}
                                            />
                                        </div>
                                        <span>
                                            <FontAwesomeIcon
                                                icon={faBan}
                                                style={{
                                                    position: "absolute",
                                                    top: "75%",
                                                    left: "93%",
                                                    transform: "translateY(-50%)",
                                                    pointerEvents: "none",
                                                }}
                                            />
                                        </span>
                                    </div>

                                    <div className="form-group">
                                        <label>Name</label>
                                        <input
                                            className="form-control"
                                            type="text"
                                            name="name"
                                            value={editedUser.name}
                                            onChange={handleEditUserInputChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Role</label>
                                        <select
                                            className="custom-select"
                                            name="role"
                                            value={editedUser.role}
                                            onChange={handleEditUserInputChange}
                                        >
                                            <option value="Manager">Manager</option>
                                            <option value="Staff">Normal User</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <button onClick={(e) => { e.preventDefault(); ClickUserInviteLink() }} type="button" className="btn cur-p btn-outline-dark" style={{ width: "50%" }} > Copy invitation link </button>
                                    </div>
                                    <div className="modal-footer">
                                        <input
                                            type="submit"
                                            className="btn cur-p btn-secondary"
                                            value="Cancel"
                                            onClick={handleEditUserCancel}
                                        />
                                        <input
                                            type="submit"
                                            className="btn cur-p btn-primary"
                                            value="Save"
                                            onClick={handleEditUserSubmit}
                                        />
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div >
    );
}

export default ManageUsers;